// @workflow assistant.approvals
/**
 * What an approval changes (contract "Reply blocks", "Approvals"): `changes: [{ label, from, to }]`,
 * drawn before → after in the approval card. Every change is shown (an approval never hides what
 * it does); approve or cancel only, no edits inside the card.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

const MAX_LABEL_LENGTH = 80;

const textOf = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

function cleanChange(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const label = textOf(raw.label);
  if (!label || label.length > MAX_LABEL_LENGTH) return null;
  const from = textOf(raw.from);
  const to = textOf(raw.to);
  if (from === to) return null;
  return { label, from, to };
}

/**
 * @param {unknown} raw the approval's `changes`
 * @returns {Array<{ label: string, from: string|null, to: string|null }>} a change without a label, or that changes nothing, is left out
 */
export function cleanChanges(raw) {
  return Array.isArray(raw) ? raw.map(cleanChange).filter(Boolean) : [];
}

/**
 * The approval's fields to draw beside its change rows. The server sends a changed field twice: in
 * `changes` (drawn before → after) and in `fields` as "old → new" text, which stays for clients that
 * read only fields (contract "Approvals"). The change row is the one the card draws, so a field with
 * the label of a change is left out: each field is drawn once.
 * @param {Array<{ label: string, value: string }>|undefined} fields the approval's `fields`
 * @param {Array<{ label: string }>|undefined} changes the approval's cleaned `changes`
 * @returns {Array<{ label: string, value: string }>}
 */
export function fieldsBesideChanges(fields, changes) {
  const changed = new Set((Array.isArray(changes) ? changes : []).map((change) => change.label));
  return (Array.isArray(fields) ? fields : []).filter((field) => !changed.has(field.label));
}
